import httpClient from '~/lib/http-client';

// ─── Search ───

export interface DomainPrice {
  registerPrice: number;
  renewPrice: number;
  currency: string;
  premium: boolean;
}

export interface DomainSearchResult {
  domain: string;
  available: boolean;
  premium: boolean;
  price: DomainPrice | null;
}

export interface DomainSearchResponse {
  query: string;
  results: DomainSearchResult[];
}

export async function apiSearchDomains(query: string): Promise<DomainSearchResponse> {
  const { data } = await httpClient.get<DomainSearchResponse>('/api/domains/search', { params: { query } });
  return data;
}

// ─── Managed domains (purchased + connected) ───

export interface ManagedDomain {
  /** domains-table id (purchased only) — used to open the detail drawer. Null for connected. */
  id: string | null;
  domain: string;
  source: 'purchased' | 'connected';
  status: string;
  createdAt: string | null;
  deploymentId: string | null;
  connectedSubdomain: string | null;
  deployUrl: string | null;
  connectedAt: string | null;
  sslStatus: string | null;
  registeredAt: string | null;
  expiresAt: string | null;
  autoRenew: boolean;
  pricePaid: number | null;
  currency: string | null;
}

export async function apiListDomains(): Promise<ManagedDomain[]> {
  const { data } = await httpClient.get<{ domains: ManagedDomain[] }>('/api/domains');
  return data.domains;
}

/** A single purchased domain's live status — polled after checkout return. */
export interface DomainDetail {
  id: string;
  domain: string;
  status: string;
  sslStatus: string;
  deploymentId: string | null;
  connectedSubdomain: string | null;
  deployUrl: string | null;
  registeredAt: string | null;
  expiresAt: string | null;
  errorMessage: string | null;
  pricePaid: number | null;
  currency: string | null;
  createdAt: string | null;
  paidAt: string | null;
  autoRenew: boolean;
  /** Whether auto-renew can be turned on (provider can charge a saved card). */
  autoRenewSupported: boolean;
  registrationYears: number | null;
  // Real registrar/DNS/SSL metadata (present for settled domains; null otherwise).
  registrar: string;
  registrarDomainId: string | null;
  renewPrice: number | null;
  renewalBreakdown: {
    registrarCost: number;
    markupPercent: number;
    markupAmount: number;
    total: number;
    currency: string;
  } | null;
  nameservers: string[] | null;
  dnsRecords: { type: string; name: string; value: string; ttl: number | null }[] | null;
  whoisPrivacy: string | null;
  transferLock: boolean | null;
  sslIssuer: string | null;
  sslExpiresAt: string | null;
  tlsVersion: string | null;
}

export async function apiGetDomain(id: string): Promise<DomainDetail> {
  const { data } = await httpClient.get<DomainDetail>(`/api/domains/${id}`);
  return data;
}

// ─── Purchase ───

/**
 * Start a domain purchase. The client sends ONLY the domain + the deployment to
 * connect it to — never a price. Returns the Stripe Checkout URL to redirect to;
 * registration happens later in the verified webhook, after payment.
 */
export async function apiPurchaseDomain(
  domain: string,
  deploymentId: string,
): Promise<{ url: string; domainId: string }> {
  const { data } = await httpClient.post<{ url: string; domainId: string }>('/api/domains/purchase', {
    domain,
    deploymentId,
  });
  return data;
}

/** Resume payment for a pending order — returns a fresh Stripe Checkout URL. */
export async function apiResumeDomainCheckout(id: string): Promise<{ url: string; domainId: string }> {
  const { data } = await httpClient.post<{ url: string; domainId: string }>(`/api/domains/${id}/checkout`);
  return data;
}

/** Remove a pending (unpaid) or failed domain order. */
export async function apiRemoveDomain(id: string): Promise<void> {
  await httpClient.delete(`/api/domains/${id}`);
}

/** Enable/disable auto-renewal for a registered domain (writes to the registrar). */
export async function apiSetDomainAutoRenew(id: string, enabled: boolean): Promise<{ autoRenew: boolean }> {
  const { data } = await httpClient.post<{ autoRenew: boolean }>(`/api/domains/${id}/auto-renew`, { enabled });
  return data;
}

// ─── Deployment picker (target for the domain) ───

export interface DeploymentOption {
  id: string;
  subdomain: string;
  deployUrl: string;
  status: string;
}

export async function apiListDeployments(): Promise<DeploymentOption[]> {
  const { data } = await httpClient.get<{ deployments: DeploymentOption[] }>('/api/deployments/static');
  return data.deployments;
}

// ─── Registrant (WHOIS owner) information ───

export interface RegistrantInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  country: string;
  stateProvince: string;
  city: string;
  postalCode: string;
  address1: string;
  organization?: string | null;
  address2?: string | null;
  /** True once a Spaceship contact has been created for this registrant. */
  hasContact?: boolean;
}

/** The user's registrant info, or null if they haven't provided it yet. */
export async function apiGetRegistrant(): Promise<RegistrantInfo | null> {
  const { data } = await httpClient.get<{ registrant: RegistrantInfo | null }>('/api/registrant');
  return data.registrant;
}

/** Save/update registrant info (server creates/reuses the Spaceship contact). */
export async function apiSaveRegistrant(input: RegistrantInfo): Promise<RegistrantInfo> {
  const { data } = await httpClient.post<{ registrant: RegistrantInfo }>('/api/registrant', input);
  return data.registrant;
}

// ─── Management (renewal, connect, DNS, privacy, transfers) ───

export interface DomainDnsRecord {
  id: number | null;
  type: string;
  name: string;
  value: string;
  ttl: number | null;
  priority: number | null;
}

export interface DomainDnsRecordInput {
  type: string;
  name: string;
  value: string;
  ttl?: number;
  priority?: number;
}

/** Renew-now: returns the checkout URL for a 1-year renewal at the live price. */
export async function apiRenewDomain(id: string): Promise<{ url: string; renewPriceUsd: number }> {
  const { data } = await httpClient.post<{ url: string; renewPriceUsd: number }>(`/api/domains/${id}/renew`);
  return data;
}

export async function apiConnectDomain(id: string, deploymentId: string): Promise<{ status: string }> {
  const { data } = await httpClient.post<{ status: string }>(`/api/domains/${id}/connect`, { deploymentId });
  return data;
}

export async function apiDisconnectDomain(id: string): Promise<{ status: string }> {
  const { data } = await httpClient.post<{ status: string }>(`/api/domains/${id}/disconnect`);
  return data;
}

export async function apiListDnsRecords(id: string): Promise<DomainDnsRecord[]> {
  const { data } = await httpClient.get<{ records: DomainDnsRecord[] }>(`/api/domains/${id}/dns`);
  return data.records;
}

export async function apiCreateDnsRecord(id: string, record: DomainDnsRecordInput): Promise<DomainDnsRecord> {
  const { data } = await httpClient.post<DomainDnsRecord>(`/api/domains/${id}/dns`, record);
  return data;
}

export async function apiUpdateDnsRecord(
  id: string,
  recordId: number,
  record: DomainDnsRecordInput,
): Promise<DomainDnsRecord> {
  const { data } = await httpClient.post<DomainDnsRecord>(`/api/domains/${id}/dns/${recordId}`, record);
  return data;
}

export async function apiDeleteDnsRecord(id: string, recordId: number): Promise<void> {
  await httpClient.delete(`/api/domains/${id}/dns/${recordId}`);
}

export async function apiSetWhoisPrivacy(id: string, enabled: boolean): Promise<{ whoisPrivacy: string }> {
  const { data } = await httpClient.post<{ whoisPrivacy: string }>(`/api/domains/${id}/whois-privacy`, { enabled });
  return data;
}

/** Unlocks the domain and returns the EPP/auth code for the new registrar. */
export async function apiTransferOut(id: string): Promise<{ authCode: string; note: string }> {
  const { data } = await httpClient.post<{ authCode: string; note: string }>(`/api/domains/${id}/transfer-out`);
  return data;
}

export async function apiRelockDomain(id: string): Promise<{ locked: boolean }> {
  const { data } = await httpClient.post<{ locked: boolean }>(`/api/domains/${id}/lock`);
  return data;
}

export async function apiQuoteTransferIn(domain: string): Promise<{ domain: string; transferPriceUsd: number }> {
  const { data } = await httpClient.post<{ domain: string; transferPriceUsd: number }>('/api/domains/transfers/quote', {
    domain,
  });
  return data;
}

export async function apiStartTransferIn(domain: string, authCode: string): Promise<{ url: string; domainId: string }> {
  const { data } = await httpClient.post<{ url: string; domainId: string }>('/api/domains/transfers', {
    domain,
    authCode,
  });
  return data;
}

export async function apiCancelTransferIn(id: string): Promise<{ status: string }> {
  const { data } = await httpClient.post<{ status: string }>(`/api/domains/${id}/transfer-cancel`);
  return data;
}

// ─── Email forwarding ───

export interface DomainEmailForward {
  emailBox: string;
  emailTo: string;
}

export async function apiListEmailForwards(id: string): Promise<DomainEmailForward[]> {
  const { data } = await httpClient.get<{ forwards: DomainEmailForward[] }>(`/api/domains/${id}/email-forwarding`);
  return data.forwards;
}

export async function apiCreateEmailForward(
  id: string,
  emailBox: string,
  emailTo: string,
): Promise<DomainEmailForward> {
  const { data } = await httpClient.post<DomainEmailForward>(`/api/domains/${id}/email-forwarding`, {
    emailBox,
    emailTo,
  });
  return data;
}

export async function apiUpdateEmailForward(
  id: string,
  emailBox: string,
  emailTo: string,
): Promise<DomainEmailForward> {
  const { data } = await httpClient.post<DomainEmailForward>(
    `/api/domains/${id}/email-forwarding/${encodeURIComponent(emailBox)}`,
    { emailTo },
  );
  return data;
}

export async function apiDeleteEmailForward(id: string, emailBox: string): Promise<void> {
  await httpClient.delete(`/api/domains/${id}/email-forwarding/${encodeURIComponent(emailBox)}`);
}
